import {useRef} from 'react';
import {useScroll} from 'react-use';
import {
  Link,
  useCart,
  CartLineProvider,
  CartShopPayButton,
  Money,
} from '@shopify/hydrogen';

import {Button, Text, CartLineItem, CartEmpty} from '~/components';

export function CartDetails({
  layout,
  onClose,
}: {
  layout: 'drawer' | 'page';
  onClose?: () => void;
}) {
  const {lines} = useCart();

  if (lines.length === 0) {
    return <CartEmpty onClose={onClose} layout={layout} />;
  }

  const container = {
    drawer: 'form-shopping-cart grid md:grid-cols-shopping-cart-open',
    page: 'pb-12 grid md:grid-cols-2 md:items-start gap-8 md:gap-8 lg:gap-12',
  };

  const content = {
    drawer:
      'overflow-auto transition h-drawer-height-mobile pt-6 md:py-16 md:pl-8 md:pr-16 md:h-drawer-height',
    page: 'flex-grow md:translate-y-4',
  };

  const summary = {
    drawer:
      'summary-section relative h-screen bg-accentDark pt-14 p-10 mr-[15px]',
    page: 'sticky top-nav grid gap-6 p-4 md:px-6 md:translate-y-4 bg-primary/5 rounded w-full',
  };

  return (
    <form
      className={container[layout]}
      onSubmit={(evt) => evt.preventDefault()}
    >
      <section
        aria-labelledby="cart-contents"
        className={content[layout]}
        style={{paddingBottom: 120}}
      >
        <header className="px-4 flex justify-between items-center h-nav">
          <h2 className="uppercase text-4xl tracking-wider">
            Ostoskorisi ({lines.length})
          </h2>

          <button
            type="button"
            className="hidden md:flex underline p-4 -m-4 transition text-primary hover:text-primary/50"
            onClick={onClose}
          >
            Jatka ostoksia
          </button>
        </header>
        <ul className="px-4 grid gap-6 pt-16 md:gap-10">
          {lines.map((line) => {
            return (
              <CartLineProvider key={line.id} line={line}>
                <CartLineItem onClose={onClose} />
              </CartLineProvider>
            );
          })}
        </ul>
        <section
          aria-labelledby="summary-heading"
          className="md:hidden summary-section bg-accentDark pt-14 px-4"
        >
          <h2
            id="summary-heading"
            className="font-clash text-3xl uppercase pb-16 text-white"
          >
            Kokonaissumma
          </h2>
          <OrderSummary />
          <p className="pb-10 text-xs text-right text-textTertiary font-inter">
            Sisältää veron, toimituskulut lasketaan kassalla
          </p>
          <CartCheckoutActions />
          <div className="flex h-[120px] opacity-0" style={{height: 120}}>
            <span>Give me some bottom margin...</span>
          </div>
        </section>
      </section>
      <section aria-labelledby="summary-heading" className={summary[layout]}>
        <h2
          id="summary-heading"
          className="font-clash text-3xl uppercase pb-16 text-white"
        >
          Kokonaissumma
        </h2>
        <OrderSummary />
        <p className="pb-10 text-xs text-right text-textTertiary font-inter">
          Sisältää veron, toimituskulut lasketaan kassalla
        </p>
        <CartCheckoutActions />
      </section>
    </form>
  );
}

function CartCheckoutActions() {
  const {checkoutUrl} = useCart();
  return (
    <>
      <div className="grid gap-4">
        {checkoutUrl ? (
          <Link to={checkoutUrl} prefetch={false} target="_self">
            <Button
              className="bg-white py-4 px-6 font-semibold text-black block text-center uppercase text-sm font-inter"
              as="span"
              width="full"
            >
              Siirry kassalle
            </Button>
          </Link>
        ) : null}
        <CartShopPayButton />
      </div>
    </>
  );
}

function OrderSummary() {
  const {cost} = useCart();
  return (
    <>
      <dl className="grid pb-6">
        <div className="flex items-center justify-between font-medium">
          <Text as="dt" className="text-white">
            Välisumma
          </Text>
          <Text as="dd">
            {cost?.subtotalAmount?.amount ? (
              <Money className="text-white" data={cost?.subtotalAmount} />
            ) : (
              '-'
            )}
          </Text>
        </div>
      </dl>
    </>
  );
}
